
import { User, Link as LinkIcon, PencilIcon, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { VerificationBadge } from "./VerificationBadge";

interface ProfileHeaderProps {
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  linkOne: string | null;
  linkTwo: string | null;
  isUpdatingAvatar: boolean;
  onImageSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  verificationStatus: 'none' | 'pending' | 'verified' | 'rejected' | 'verified_creative';
  isCreative: boolean;
}

export function ProfileHeader({
  username,
  displayName,
  avatarUrl,
  linkOne,
  linkTwo,
  isUpdatingAvatar,
  onImageSelect,
  verificationStatus,
  isCreative,
}: ProfileHeaderProps) {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col items-center mb-12">
      <div className="relative group">
        <Avatar className={`${isMobile ? 'h-24 w-24' : 'h-32 w-32'} rounded-lg border-2 border-border group-hover:border-primary transition-colors`}>
          <AvatarImage 
            src={avatarUrl || undefined} 
            alt={displayName || username}
            className="object-cover"
          />
          <AvatarFallback>
            <User className={`${isMobile ? 'h-8 w-8' : 'h-12 w-12'}`} />
          </AvatarFallback>
        </Avatar>
        <div className="absolute -bottom-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <input 
            type="file"
            accept="image/*"
            onChange={onImageSelect}
            className="hidden"
            id="profile-picture"
            disabled={isUpdatingAvatar}
          />
          <Button
            size="icon"
            variant="default"
            className="rounded-full shadow-lg"
            disabled={isUpdatingAvatar}
            onClick={() => document.getElementById('profile-picture')?.click()}
          >
            {isUpdatingAvatar ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PencilIcon className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold`}>{displayName || username}</h1>
        <p className="text-muted-foreground">@{username}</p>
        <VerificationBadge 
          status={verificationStatus} 
          isCreative={isCreative} 
          showText={true}
        />
      </div>
      {(linkOne || linkTwo) && (
        <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'gap-4'} mt-6`}>
          {linkOne && (
            <Button
              variant="outline"
              asChild
              className={`gap-2 ${isMobile ? 'w-full' : ''}`}
            >
              <a href={linkOne} target="_blank" rel="noopener noreferrer">
                <LinkIcon className="h-4 w-4" />
                {new URL(linkOne).hostname}
              </a>
            </Button>
          )}
          {linkTwo && (
            <Button
              variant="outline"
              asChild
              className={`gap-2 ${isMobile ? 'w-full' : ''}`}
            >
              <a href={linkTwo} target="_blank" rel="noopener noreferrer">
                <LinkIcon className="h-4 w-4" />
                {new URL(linkTwo).hostname}
              </a>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
